import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  Initiative,
  InitiativeWorkstream,
  InitiativeMilestone,
  InitiativeActionBridge,
  InitiativeProgressUpdate,
  InitiativeRisk,
  InitiativeClosureRecord,
  InitiativeStatusHistory,
  InitiativeHealthHistory,
  InitiativeVersion
} from './types';
import { initiativesService } from './initiativesService';

interface Props {
  initiative: Initiative;
  onClose: () => void;
  onRefresh: () => void;
}

type TabType = 'overview' | 'workstreams' | 'milestones' | 'actions' | 'progress' | 'risks' | 'closure' | 'history';

export const InitiativeDetailModal: React.FC<Props> = ({ initiative, onClose, onRefresh }) => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [workstreams, setWorkstreams] = useState<InitiativeWorkstream[]>([]);
  const [milestones, setMilestones] = useState<InitiativeMilestone[]>([]);
  const [actions, setActions] = useState<InitiativeActionBridge[]>([]);
  const [progressUpdates, setProgressUpdates] = useState<InitiativeProgressUpdate[]>([]);
  const [risks, setRisks] = useState<InitiativeRisk[]>([]);
  const [closureRecord, setClosureRecord] = useState<InitiativeClosureRecord | null>(null);
  const [statusHistory, setStatusHistory] = useState<InitiativeStatusHistory[]>([]);
  const [healthHistory, setHealthHistory] = useState<InitiativeHealthHistory[]>([]);
  const [versions, setVersions] = useState<InitiativeVersion[]>([]);
  const [readiness, setReadiness] = useState<any>(null);

  // Forms state
  const [transitionStatus, setTransitionStatus] = useState<string>('ACTIVE');
  const [transitionReason, setTransitionReason] = useState<string>('');

  // Milestone Complete Form
  const [completingMilestoneId, setCompletingMilestoneId] = useState<string | null>(null);
  const [evidenceRef, setEvidenceRef] = useState<string>('');

  // Progress Update Form
  const [reportedProgress, setReportedProgress] = useState<number>(initiative.progress_percentage);
  const [narrativeSummary, setNarrativeSummary] = useState<string>('');
  const [accomplishments, setAccomplishments] = useState<string>('');
  const [roadblocks, setRoadblocks] = useState<string>('');

  // Closure Form
  const [sponsorSignoff, setSponsorSignoff] = useState<boolean>(true);
  const [deliverablesAccepted, setDeliverablesAccepted] = useState<boolean>(true);
  const [budgetReconciled, setBudgetReconciled] = useState<boolean>(true);
  const [effectivenessVerified, setEffectivenessVerified] = useState<boolean>(true);
  const [outcomeScore, setOutcomeScore] = useState<number>(95);
  const [closureSummary, setClosureSummary] = useState<string>('');

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadTabData = async () => {
    try {
      if (activeTab === 'workstreams') {
        const ws = await initiativesService.getWorkstreams(initiative.id);
        setWorkstreams(ws);
      } else if (activeTab === 'milestones') {
        const ms = await initiativesService.getMilestones(initiative.id);
        setMilestones(ms);
      } else if (activeTab === 'actions') {
        const ac = await initiativesService.getLinkedActions(initiative.id);
        setActions(ac);
      } else if (activeTab === 'progress') {
        const pu = await initiativesService.getProgressUpdates(initiative.id);
        setProgressUpdates(pu);
      } else if (activeTab === 'risks') {
        const rk = await initiativesService.getRisks(initiative.id);
        setRisks(rk);
      } else if (activeTab === 'closure') {
        const cl = await initiativesService.getClosureRecord(initiative.id);
        setClosureRecord(cl);
        const rd = await initiativesService.checkClosureReadiness(initiative.id);
        setReadiness(rd);
      } else if (activeTab === 'history') {
        const sh = await initiativesService.getStatusHistory(initiative.id);
        const hh = await initiativesService.getHealthHistory(initiative.id);
        const vr = await initiativesService.getVersions(initiative.id);
        setStatusHistory(sh);
        setHealthHistory(hh);
        setVersions(vr);
      }
    } catch (err: any) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadTabData();
  }, [activeTab, initiative.id]);

  const handleStatusTransition = async () => {
    try {
      setLoading(true);
      setMessage(null);
      await initiativesService.transitionStatus(initiative.id, transitionStatus, transitionReason || 'Status transitioned');
      setMessage({ type: 'success', text: `Initiative status updated to '${transitionStatus}'.` });
      setTransitionReason('');
      onRefresh();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteMilestone = async (mId: string) => {
    try {
      setLoading(true);
      await initiativesService.completeMilestone(initiative.id, mId, evidenceRef || 'Completed', 'Authorized Specialist');
      setCompletingMilestoneId(null);
      setEvidenceRef('');
      loadTabData();
      onRefresh();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRecordProgress = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await initiativesService.recordProgressUpdate(initiative.id, {
        reported_progress: Number(reportedProgress),
        narrative_summary: narrativeSummary,
        accomplishments,
        roadblocks,
        reported_by_name: 'Project Manager'
      });
      setMessage({ type: 'success', text: 'Progress report submitted successfully.' });
      setNarrativeSummary('');
      setAccomplishments('');
      setRoadblocks('');
      loadTabData();
      onRefresh();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseInitiative = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await initiativesService.closeInitiative(initiative.id, {
        closed_by_id: 'USR-DIR-01',
        closed_by_name: 'Cemil Demir (Accountable Manager)',
        milestones_completed: true,
        actions_completed: true,
        deliverables_accepted: deliverablesAccepted,
        budget_reconciled: budgetReconciled,
        sponsor_signoff: sponsorSignoff,
        delivery_rating: 'SUCCESSFUL',
        outcome_achievement_score: Number(outcomeScore),
        effectiveness_verified: effectivenessVerified,
        closure_summary: closureSummary || 'Initiative closed successfully with verified deliverables and alignment.'
      });
      setMessage({ type: 'success', text: 'Initiative formally closed.' });
      loadTabData();
      onRefresh();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                {initiative.initiative_number}
              </span>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs text-gray-500 font-mono">v{initiative.version}</span>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs font-semibold px-2 py-0.5 rounded bg-indigo-100 text-indigo-800">
                {initiative.initiative_type}
              </span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                initiative.health_status === 'ON_TRACK'
                  ? 'bg-emerald-100 text-emerald-800'
                  : initiative.health_status === 'CRITICAL'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-amber-100 text-amber-800'
              }`}>
                HEALTH: {initiative.health_status} ({initiative.health_score}%)
              </span>
            </div>
            <h3>{initiative.title}</h3>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 px-6 gap-2 bg-gray-50 overflow-x-auto text-xs font-medium">
          {[
            { id: 'overview', label: 'Overview & Status' },
            { id: 'workstreams', label: 'Workstreams' },
            { id: 'milestones', label: 'Milestones' },
            { id: 'actions', label: 'Actions Bridge' },
            { id: 'progress', label: 'Progress Log' },
            { id: 'risks', label: 'Risks & Roadblocks' },
            { id: 'closure', label: 'Formal Closure' },
            { id: 'history', label: 'Versions & Audit' }
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`py-3 px-3 border-b-2 transition ${
                activeTab === tab.id ? 'border-blue-600 text-blue-700 font-bold' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {message && (
            <div className={`p-3 rounded-lg text-xs border ${
              message.type === 'success' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-red-50 text-red-800 border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          {/* 1. OVERVIEW */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-4">
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-xs">
                  <div className="mb-3">
                    <span className="text-gray-500 font-semibold uppercase block mb-1">Description</span>
                    <p className="text-gray-800 text-sm leading-relaxed">{initiative.description}</p>
                  </div>
                  {initiative.business_case && (
                    <div className="mb-3 pt-3 border-t border-gray-200">
                      <span className="text-gray-500 font-semibold uppercase block mb-1">Business Case & Rationale</span>
                      <p className="text-gray-700">{initiative.business_case}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-gray-200">
                    <div><span className="text-gray-500">Department:</span> <strong className="text-gray-900 ml-1">{initiative.target_department}</strong></div>
                    <div><span className="text-gray-500">Priority:</span> <strong className="text-gray-900 ml-1">{initiative.priority}</strong></div>
                    <div><span className="text-gray-500">Start Date:</span> <strong className="text-gray-900 ml-1 font-mono">{initiative.planned_start_date}</strong></div>
                    <div><span className="text-gray-500">Target Date:</span> <strong className="text-gray-900 ml-1 font-mono">{initiative.planned_end_date}</strong></div>
                    <div><span className="text-gray-500">Estimated Budget:</span> <strong className="text-gray-900 ml-1">${initiative.estimated_budget.toLocaleString()}</strong></div>
                    <div><span className="text-gray-500">Actual Cost:</span> <strong className="text-gray-900 ml-1">${initiative.actual_cost.toLocaleString()}</strong></div>
                    <div><span className="text-gray-500">Sponsor:</span> <strong className="text-gray-900 ml-1">{initiative.sponsor_name}</strong></div>
                    <div><span className="text-gray-500">Owner:</span> <strong className="text-gray-900 ml-1">{initiative.owner_name}</strong></div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900">Status Transition</h4>
                  <div>
                    <span className="text-xs text-gray-500 block">Current Status:</span>
                    <span className="font-bold text-blue-700 text-sm">{initiative.status}</span>
                  </div>

                  {!initiative.is_closed ? (
                    <>
                      <div className="form-field">
                        <label>Select Target Status</label>
                        <select
                          value={transitionStatus}
                          onChange={(e) => setTransitionStatus(e.target.value)}
                        >
                          <option value="SUBMITTED">SUBMITTED</option>
                          <option value="APPROVED">APPROVED</option>
                          <option value="ACTIVE">ACTIVE</option>
                          <option value="ON_HOLD">ON HOLD</option>
                          <option value="UNDER_REVIEW">UNDER REVIEW</option>
                          <option value="CANCELLED">CANCELLED</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label>Transition Reason</label>
                        <input
                          type="text"
                          value={transitionReason}
                          onChange={(e) => setTransitionReason(e.target.value)}
                          placeholder="Reason for change..."
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleStatusTransition}
                        disabled={loading}
                        className="btn-primary w-full"
                      >
                        {loading ? 'Updating...' : 'Update Status'}
                      </button>
                    </>
                  ) : (
                    <div className="p-3 bg-emerald-50 rounded-lg text-emerald-800 text-xs font-semibold border border-emerald-200">
                      This initiative is formally closed.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 2. WORKSTREAMS */}
          {activeTab === 'workstreams' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Project Workstreams</h3>
              {workstreams.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-xs">No registered workstreams found.</div>
              ) : (
                <div className="space-y-3">
                  {workstreams.map((ws) => (
                    <div key={ws.id} className="p-4 bg-white rounded-xl border border-gray-200 flex justify-between items-center text-xs">
                      <div>
                        <div className="font-bold text-gray-900 text-sm">{ws.workstream_code} - {ws.title}</div>
                        <div className="text-gray-500 text-[11px] mt-1">Lead: {ws.lead_user_name} | Weight: {ws.weight}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-gray-700">{ws.progress_percentage}%</span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">{ws.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 3. MILESTONES */}
          {activeTab === 'milestones' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Milestone Deliverables</h3>
              {milestones.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-xs">No registered milestones found.</div>
              ) : (
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-b border-gray-200 text-left text-gray-600 font-semibold">
                      <tr>
                        <th className="p-3">Code</th>
                        <th className="p-3">Title</th>
                        <th className="p-3">Target Date</th>
                        <th className="p-3">Critical Path</th>
                        <th className="p-3">Status</th>
                        <th className="p-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {milestones.map((ms) => (
                        <tr key={ms.id} className="hover:bg-gray-50">
                          <td className="p-3 font-semibold font-mono text-gray-900">{ms.milestone_code}</td>
                          <td className="p-3">
                            <div className="font-semibold text-gray-900">{ms.title}</div>
                            {ms.evidence_reference && (
                              <div className="text-[11px] text-emerald-700 mt-0.5">Evidence: {ms.evidence_reference}</div>
                            )}
                          </td>
                          <td className="p-3 font-mono text-gray-600">{ms.due_date}</td>
                          <td className="p-3">
                            {ms.is_critical_path ? <span className="text-red-700 font-bold">YES</span> : 'No'}
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              ms.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                            }`}>
                              {ms.status}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            {ms.status !== 'COMPLETED' && (
                              completingMilestoneId === ms.id ? (
                                <div className="flex justify-end gap-1">
                                  <input
                                    type="text"
                                    placeholder="Evidence ref..."
                                    value={evidenceRef}
                                    onChange={(e) => setEvidenceRef(e.target.value)}
                                    className="p-1 text-xs border border-gray-300 rounded w-28"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleCompleteMilestone(ms.id)}
                                    className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[11px] font-semibold"
                                  >
                                    Approve
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setCompletingMilestoneId(ms.id)}
                                  className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[11px] font-semibold"
                                >
                                  Complete
                                </button>
                              )
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* 4. ACTIONS BRIDGE */}
          {activeTab === 'actions' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Linked Central Action Items</h3>
              {actions.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-xs">No linked central actions found.</div>
              ) : (
                <div className="space-y-2">
                  {actions.map((ac) => (
                    <div key={ac.id} className="p-3 bg-white rounded-xl border border-gray-200 flex justify-between items-center text-xs">
                      <div>
                        <div className="font-semibold text-gray-900">{ac.action_code}: {ac.title}</div>
                        <div className="text-gray-500 text-[11px] mt-0.5">Relation: {ac.relationship_type} | Weight: {ac.weight}</div>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        ac.contribution_status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {ac.contribution_status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 5. PROGRESS UPDATES */}
          {activeTab === 'progress' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Record Progress Update</h3>
                <form onSubmit={handleRecordProgress} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                  <div className="form-field">
                    <label>Reported Progress (%) *</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={reportedProgress}
                      onChange={(e) => setReportedProgress(Number(e.target.value))}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Narrative Summary *</label>
                    <textarea
                      rows={3}
                      value={narrativeSummary}
                      onChange={(e) => setNarrativeSummary(e.target.value)}
                      placeholder="Summary of operational achievements, team activities..."
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Key Accomplishments</label>
                    <input
                      type="text"
                      value={accomplishments}
                      onChange={(e) => setAccomplishments(e.target.value)}
                      placeholder="Milestones or deliverables reached..."
                    />
                  </div>
                  <div className="form-field">
                    <label>Roadblocks & Impediments</label>
                    <input
                      type="text"
                      value={roadblocks}
                      onChange={(e) => setRoadblocks(e.target.value)}
                      placeholder="Pending approvals, resource constraints..."
                    />
                  </div>
                  <div className="flex justify-end pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary"
                    >
                      Save Progress Update
                    </button>
                  </div>
                </form>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Historical Updates</h3>
                {progressUpdates.length === 0 ? (
                  <div className="text-center py-6 text-gray-500 text-xs">No progress updates submitted yet.</div>
                ) : (
                  <div className="space-y-3 max-h-[420px] overflow-y-auto">
                    {progressUpdates.map((pu) => (
                      <div key={pu.id} className="p-3 bg-white rounded-xl border border-gray-200 text-xs space-y-1">
                        <div className="flex justify-between items-center font-semibold">
                          <span className="text-blue-700">{pu.reported_progress}% (Calculated: {pu.calculated_progress}%)</span>
                          <span className="text-gray-500 font-mono text-[11px]">{pu.period_date}</span>
                        </div>
                        <p className="text-gray-800">{pu.narrative_summary}</p>
                        {pu.accomplishments && <div className="text-emerald-700 text-[11px]">✔ {pu.accomplishments}</div>}
                        {pu.roadblocks && <div className="text-red-700 text-[11px]">✖ {pu.roadblocks}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 6. RISKS */}
          {activeTab === 'risks' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Initiative Risks & Impediments</h3>
              {risks.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-xs">No active risk entries recorded.</div>
              ) : (
                <div className="space-y-2">
                  {risks.map((r) => (
                    <div key={r.id} className="p-3 bg-white rounded-xl border border-gray-200 flex justify-between items-center text-xs">
                      <div>
                        <div className="font-semibold text-gray-900">{r.risk_title}</div>
                        <div className="text-gray-500 text-[11px] mt-0.5">
                          Risk Index: {r.risk_index} | Severity: {r.severity} | Likelihood: {r.likelihood}
                        </div>
                        {r.mitigation_strategy && (
                          <div className="text-blue-700 text-[11px] mt-1">Mitigation: {r.mitigation_strategy}</div>
                        )}
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        r.is_active ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {r.is_active ? 'ACTIVE' : 'RESOLVED'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 7. FORMAL CLOSURE */}
          {activeTab === 'closure' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Formal Closure & Effectiveness Assurance</h3>
              {closureRecord ? (
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 text-xs space-y-2">
                  <div className="font-bold text-emerald-900 text-sm">
                    ✔ INITIATIVE FORMALLY CLOSED ({closureRecord.delivery_rating})
                  </div>
                  <div className="text-emerald-800">Closure Date: {closureRecord.closed_at}</div>
                  <div className="text-emerald-800">Closed By: {closureRecord.closed_by_name}</div>
                  <div className="text-emerald-800">Outcome Achievement Score: {closureRecord.outcome_achievement_score}/100</div>
                  <div className="text-emerald-800">Effectiveness Verified: {closureRecord.effectiveness_verified ? 'Yes' : 'No'}</div>
                  <div className="text-emerald-900 mt-2 font-medium">Summary: {closureRecord.closure_summary}</div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 uppercase mb-3">Closure Readiness Gates</h4>
                    {readiness && (
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-xs space-y-2">
                        <div>Overall Progress: <strong className="text-gray-900">{readiness.progress_percentage}% (Required: 100%)</strong></div>
                        <div>Open Milestones: <strong className="text-gray-900">{readiness.uncompleted_milestones}</strong></div>
                        <div>Open Linked Actions: <strong className="text-gray-900">{readiness.uncompleted_actions}</strong></div>
                        {readiness.reasons && readiness.reasons.length > 0 && (
                          <div className="text-red-700 mt-3 pt-3 border-t border-gray-200 space-y-1">
                            {readiness.reasons.map((r: string, idx: number) => (
                              <div key={idx}>✖ {r}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 uppercase mb-3">Closure Verification Form</h4>
                    <form onSubmit={handleCloseInitiative} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3 text-xs">
                      <label className="flex items-center gap-2 cursor-pointer text-gray-700">
                        <input
                          type="checkbox"
                          checked={sponsorSignoff}
                          onChange={(e) => setSponsorSignoff(e.target.checked)}
                          className="rounded text-blue-600 border-gray-300"
                        />
                        Executive Sponsor Formal Signoff
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-gray-700">
                        <input
                          type="checkbox"
                          checked={deliverablesAccepted}
                          onChange={(e) => setDeliverablesAccepted(e.target.checked)}
                          className="rounded text-blue-600 border-gray-300"
                        />
                        Deliverables Verified & Accepted
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-gray-700">
                        <input
                          type="checkbox"
                          checked={budgetReconciled}
                          onChange={(e) => setBudgetReconciled(e.target.checked)}
                          className="rounded text-blue-600 border-gray-300"
                        />
                        Final Budget Reconciled
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-gray-700">
                        <input
                          type="checkbox"
                          checked={effectivenessVerified}
                          onChange={(e) => setEffectivenessVerified(e.target.checked)}
                          className="rounded text-blue-600 border-gray-300"
                        />
                        Operational Effectiveness Confirmed
                      </label>
                      <div className="form-field">
                        <label>Outcome Score (0-100)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={outcomeScore}
                          onChange={(e) => setOutcomeScore(Number(e.target.value))}
                          required
                        />
                      </div>
                      <div className="form-field">
                        <label>Closure Summary *</label>
                        <textarea
                          rows={3}
                          value={closureSummary}
                          onChange={(e) => setClosureSummary(e.target.value)}
                          placeholder="Final executive summary of outcomes and operational handover..."
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold shadow-sm w-full transition"
                      >
                        Formally Close Initiative
                      </button>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 8. AUDIT HISTORY & VERSIONS */}
          {activeTab === 'history' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase mb-3">Status Transition Audit Trail</h4>
                <div className="space-y-2">
                  {statusHistory.map((sh) => (
                    <div key={sh.id} className="p-3 bg-white rounded-xl border border-gray-200 text-xs">
                      <div className="font-semibold text-blue-700">{sh.from_status} → {sh.to_status}</div>
                      <div className="text-gray-600 mt-0.5">{sh.reason}</div>
                      <div className="text-[10px] text-gray-400 mt-1 font-mono">{sh.changed_by_name} ({sh.created_at})</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase mb-3">Immutable Version Snapshots</h4>
                <div className="space-y-2">
                  {versions.map((vr) => (
                    <div key={vr.id} className="p-3 bg-white rounded-xl border border-gray-200 text-xs">
                      <div className="font-semibold text-gray-900">Version {vr.version_number}: {vr.title}</div>
                      <div className="text-gray-600 mt-0.5">{vr.version_reason}</div>
                      <div className="text-[10px] text-gray-400 mt-1 font-mono">{vr.created_by} ({vr.created_at})</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
